import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "../lib/auth";
import { Book } from "./Book";
import { Rail } from "./Rail";
import { BuildingPanel } from "./BuildingPanel";
import { IntakeWizard } from "./IntakeWizard";
import { DecisionQueue } from "./DecisionQueue";
import { Settings } from "./Settings";
import { Lockup } from "../components/Lockup";
import { plural } from "./format";
import { bindDraftOwner } from "./drafts";
import { useLiveDesk } from "./useLiveDesk";

// The owner desk, built to `Juno Owner v4.dc.html` (Release 1, owner seat): a dark top bar, the
// left rail (you + your portfolio), the main panel (the agents, what needs you, the book), and one
// right-hand slide-over that every selection opens into.
//
// LIGHT ONLY (founder ruling 27 Sep). There is no theme toggle and nothing here reads a stored
// preference or the OS — the `desk-owner` class carries the light token map (theme.css), and the
// black panels carry `data-juno-ink="dark"`. The staff console stays dark until Part 2.
//
// Behaviour is V4-PRODUCTION-PARITY.md and is unchanged by the reskin: the same one desk read, the
// same in-flight polling, the same deep link, the same decision queue, the same book.

function initials(name) {
  const parts = String(name || "").trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return "—";
  return (parts[0][0] + (parts.length > 1 ? parts[parts.length - 1][0] : "")).toUpperCase();
}

function AccountMenu({ name, onSettings, onSignOut }) {
  const [open, setOpen] = useState(false);
  const box = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const away = (e) => { if (box.current && !box.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", away);
    return () => document.removeEventListener("mousedown", away);
  }, [open]);
  return (
    <div className="v4-account" ref={box}>
      <button className="v4-avatar" data-testid="desk-account" title="Your account"
              onClick={() => setOpen((v) => !v)}>{initials(name)}</button>
      {open && (
        <div className="v4-menu" data-testid="desk-account-menu">
          <div className="v4-menu-name" data-testid="desk-owner-name">{name}</div>
          <button className="v4-menu-item" data-testid="desk-settings"
                  onClick={() => { setOpen(false); onSettings(); }}>Settings</button>
          <button className="v4-menu-item" data-testid="desk-sign-out"
                  onClick={() => { setOpen(false); onSignOut(); }}>Sign out</button>
        </div>
      )}
    </div>
  );
}

function AgentTile({ a }) {
  return (
    <div className={"v4-tile" + (a.live ? " v4-tile-live" : " v4-tile-inert")}
         data-juno-ink={a.live ? "dark" : undefined}
         data-testid={"agent-" + a.name.replace(/\s+/g, "-").toLowerCase()}>
      <div className="v4-tile-top">
        {/* F121: while a re-read runs anywhere on this desk, the agent doing it says so. */}
        <span className={"v4-tile-dot" + (a.live ? " v4-tile-dot-live" : "")
                         + (a.working ? " v4-tile-dot-working" : "")} />
        <span className="v4-tile-name">{a.name}</span>
      </div>
      <span className="v4-tile-chip" data-testid={a.working ? "agent-working" : undefined}>
        {a.working ? "WORKING NOW" : a.live ? "LIVE" : a.release.toUpperCase()}
      </span>
      <p className="v4-tile-now">{a.doing}</p>
    </div>
  );
}

// F114 (founder 30 Sep): the desk used to sit on a blank "Loading your desk…" page while the one
// read landed. The FRAME is not data — the top bar, the rail, the headings and the book's own
// outline are drawn immediately and the numbers fill in. Nothing here asserts a value it doesn't
// have: a skeleton cell is an empty outline, never a zero and never a placeholder number.
function RailSkeleton() {
  return (
    <aside className="v4-rail" data-testid="rail-skeleton" aria-hidden="true">
      <div className="v4-you">
        <span className="v4-you-eyebrow">YOU</span>
        <div className="v4-sk v4-sk-line" />
      </div>
      <div className="v4-card" data-juno-ink="dark">
        <span className="v4-card-edge" />
        <div className="v4-card-body">
          <div className="v4-card-top"><span className="v4-card-label">YOUR PORTFOLIO</span></div>
          <div className="v4-sk v4-sk-xl" />
          <div className="v4-sk v4-sk-chart" />
          <div className="v4-sk v4-sk-line" />
        </div>
      </div>
      <div className="v4-feed">
        <div className="v4-feed-head">
          <span className="v4-feed-title">CONSOLIDATED ACTIVITY</span>
        </div>
        <div className="v4-sk v4-sk-line" />
        <div className="v4-sk v4-sk-line" />
        <div className="v4-sk v4-sk-line" />
      </div>
    </aside>
  );
}

function BookSkeleton() {
  return (
    <div className="book" data-testid="book-skeleton" aria-hidden="true">
      <table className="book-table">
        <thead>
          <tr>
            <th className="book-th book-th-left">Building</th>
            <th className="book-th book-th-left">Disposition</th>
            <th className="book-th">Your number</th>
            <th className="book-th">The mark</th>
            <th className="book-th">Matched</th>
            <th className="book-th">Structured</th>
            <th className="book-th">Closed</th>
          </tr>
        </thead>
        <tbody>
          {[0, 1, 2].map((i) => (
            <tr className="book-row" key={i}>
              {[0, 1, 2, 3, 4, 5, 6].map((c) => (
                <td className="book-cell" key={c}><span className="v4-sk v4-sk-cell" /></td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function OwnerDesk() {
  const { logout, authedFetch, session } = useAuth();
  // Bind the draft store to THIS account: a different account signing in on this tab wipes any drafts
  // held from a prior session (Step C Part 6).
  useEffect(() => { bindDraftOwner(session?.account_id); }, [session?.account_id]);
  const [desk, setDesk] = useState(null);
  const [err, setErr] = useState("");
  const [openRef, setOpenRef] = useState(null);
  const [wizard, setWizard] = useState(false);
  const [settings, setSettings] = useState(false);

  const load = useCallback(() => {
    authedFetch("/owner/desk").then(setDesk).catch((e) => setErr(e.detail || "Could not load your desk."));
  }, [authedFetch]);
  const reload = useCallback(() => { setErr(""); load(); }, [load]);
  useEffect(() => { load(); }, [load]);
  // F139 (founder item 9): the live channel. See desk/useLiveDesk.js for what it is and why the old
  // bounded poll stopped firing on Crenshaw and Bammel.
  useLiveDesk(desk, load);

  // Decision-email deep link (Step D ③): ?b=<building id>. Open that building's file ONLY if it is on
  // THIS account's desk; otherwise stay on desk home and reveal nothing (the book lists owned buildings
  // only, and the building API is account-scoped). No token is ever in the link.
  const deeplinkHandled = useRef(false);
  useEffect(() => {
    if (!desk || deeplinkHandled.current) return;
    deeplinkHandled.current = true;
    const b = new URLSearchParams(window.location.search).get("b")
              || sessionStorage.getItem("juno_pending_b");   // F59: survives a sign-in that dropped the URL
    sessionStorage.removeItem("juno_pending_b");
    if (b && (desk.book?.rows || []).some((r) => r.building_ref === b)) setOpenRef(b);
    if (b) window.history.replaceState({}, "", window.location.pathname + window.location.hash);
  }, [desk]);

  // THE LIVE CHANNEL IS A RE-FETCH OF /owner/desk (desk/useLiveDesk.js). A mark is produced by an
  // ASYNC worker after an upload (pipeline: file.filed → extract → persist), so the desk re-reads
  // until it lands — or the BOOK shows "—" (and the portfolio total excludes it) while the building
  // FILE fetches fresh and shows the mark: the book/file disagreement (founder 20 Sep). The cadence,
  // the states that count as in flight and the clock that no longer runs out all live in the hook.

  if (err) {
    return <div className="desk-owner desk-error" data-testid="desk-error">
      <p className="desk-error-msg">{err}</p>
      <div className="desk-error-actions">
        <button className="chrome-btn chrome-btn-primary" data-testid="desk-error-retry"
                onClick={reload}>Try again</button>
        <button className="chrome-btn" data-testid="desk-sign-out-error" onClick={() => logout()}>Sign out</button>
      </div>
      <p className="desk-error-support">Still stuck? Email <a href="mailto:support@junocre.com"
         data-testid="desk-error-support">support@junocre.com</a>.</p>
    </div>;
  }
  if (!desk) {
    // The frame, immediately (F114). `desk-loading` marks it so a test can tell the two apart.
    return (
      <div className="desk-owner" data-testid="desk-loading">
        <header className="v4-top" data-juno-ink="dark" data-testid="desk-top">
          <Lockup size="header" />
          <span className="v4-top-rule" />
          <span className="v4-top-seat">Owner desk</span>
          <span className="v4-top-spacer" />
          <div className="v4-live" data-testid="desk-live-wait">
            <span className="v4-live-dot" />
            <span className="v4-live-text">READING YOUR DESK</span>
          </div>
          <span className="v4-avatar v4-avatar-wait" aria-hidden="true" />
        </header>
        <div className="v4-grid">
          <RailSkeleton />
          <main className="v4-main" data-testid="desk-main">
            <div className="v4-main-head">
              <span className="v4-main-eyebrow">JUNO · THE AGENTS</span>
              <span className="v4-hr" />
            </div>
            <div className="v4-tiles" aria-hidden="true">
              {[0, 1, 2, 3].map((i) => (
                <div className="v4-tile v4-tile-inert" key={i}>
                  <div className="v4-sk v4-sk-line" />
                  <div className="v4-sk v4-sk-line" />
                </div>
              ))}
            </div>
            <div className="v4-board" data-testid="region-boards">
              <div className="v4-board-head">
                <span className="v4-tab">THE BOOK</span>
                <span className="v4-tab v4-tab-inert">DEALS IN MOTION · RELEASE 2</span>
                <span className="v4-hr" />
                <span className="v4-board-note">reading your book</span>
              </div>
              <div className="v4-board-panel"><BookSkeleton /></div>
            </div>
          </main>
        </div>
      </div>
    );
  }

  const decisions = desk.decisions.items || [];
  const decideCount = desk.decisions.count;
  const agents = desk.agents || [];
  const librarian = agents.find((a) => a.name === "The Librarian");
  const tiles = agents.filter((a) => a.name !== "The Librarian");
  const liveCount = agents.filter((a) => a.live).length;
  const scrollToDecide = () => {
    const el = document.querySelector('[data-testid="region-decide"]');
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="desk-owner" data-testid="owner-desk">
      <header className="v4-top" data-juno-ink="dark" data-testid="desk-top">
        <Lockup size="header" />
        <span className="v4-top-rule" />
        <span className="v4-top-seat">Owner desk</span>
        <span className="v4-top-spacer" />
        <div className="v4-live" data-testid="desk-live-chip">
          <span className="v4-live-dot" />
          <span className="v4-live-text">{liveCount} AGENTS LIVE</span>
          {decideCount > 0 && (
            <>
              <span className="v4-live-sep" />
              <button className="v4-needs" data-testid="needs-chip" onClick={scrollToDecide}>
                {decideCount} NEED{decideCount === 1 ? "S" : ""} YOU
              </button>
            </>
          )}
        </div>
        {/* F-addendum 30 Sep: Add a building is the RAIL's primary action, not the header's. The
            header keeps the name (in the account menu), Settings and Sign out. */}
        <AccountMenu name={desk.identity.name} onSettings={() => setSettings(true)}
                     onSignOut={() => logout()} />
      </header>

      <div className="v4-grid">
        <Rail desk={desk} onOpen={setOpenRef} onAdd={() => setWizard(true)} />

        <main className="v4-main" data-testid="desk-main">
          <div className="v4-main-head">
            <span className="v4-main-eyebrow">JUNO · THE AGENTS</span>
            <span className="v4-hr" />
          </div>

          {!desk.empty && decideCount > 0 && (
            <div className="v4-strip" data-testid="decision-strip">
              <div className="v4-strip-top">
                <span className="v4-strip-dot" />
                <span className="v4-strip-title">
                  {decideCount === 1 ? "ONE DECISION IS YOURS" : decideCount + " DECISIONS ARE YOURS"}
                </span>
                <span className="v4-strip-note">NOTHING MOVES UNTIL YOU SAY</span>
              </div>
              <div className="v4-strip-rows">
                {decisions.map((d) => (
                  <button className="v4-strip-row" key={d.id} onClick={scrollToDecide}
                          data-testid={"strip-decision-" + d.id}>
                    <span className="v4-strip-b">{d.building_label}</span>
                    <span className="v4-strip-w">{d.prompt}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {librarian && (
            <div className="v4-lib" data-juno-ink="dark" data-testid="agent-the-librarian">
              <span className="v4-lib-dot" />
              <span className="v4-lib-name">THE LIBRARIAN</span>
              <span className="v4-lib-line">{librarian.doing}</span>
              <span className="v4-lib-chip">{librarian.live ? "LIVE" : librarian.release}</span>
            </div>
          )}

          <div className="v4-tiles" data-testid="agent-grid">
            {tiles.map((a) => <AgentTile key={a.name} a={a} />)}
          </div>

          <div className="v4-board" data-testid="region-boards">
            <div className="v4-board-head">
              <span className="v4-tab">THE BOOK</span>
              <span className="v4-tab v4-tab-inert">DEALS IN MOTION · RELEASE 2</span>
              <span className="v4-hr" />
              <span className="v4-board-note">
                {desk.empty ? "nothing on it yet" : "select a row to open its file"}
              </span>
            </div>
            <div className="v4-board-panel">
              {desk.empty ? (
                <div className="v4-empty" data-testid="desk-empty-state">
                  <span className="v4-empty-eyebrow">THE BOOK · NOTHING ON IT YET</span>
                  <h1 className="v4-empty-h">Bring me your first building.</h1>
                  <div className="v4-empty-box" data-juno-ink="dark">
                    <span className="v4-empty-box-label">START WITH THE ADDRESS</span>
                    <button className="v4-add" data-testid="desk-add-first"
                            onClick={() => setWizard(true)}>Add a building</button>
                  </div>
                  <p className="v4-empty-lead">
                    Give me the address, then an offering memo, rent roll or T-12 as a PDF or .xlsx
                    (.xls isn't readable — save as .xlsx). Every line of the mark traces back to a page
                    you gave me.
                  </p>
                  <div className="v4-empty-steps">
                    <div className="v4-empty-step">
                      <span className="v4-empty-step-k">1 · THE LIBRARIAN</span>
                      <span className="v4-empty-step-v">Reads and files what you send, and says what
                        it could not place.</span>
                    </div>
                    <div className="v4-empty-step">
                      <span className="v4-empty-step-k">2 · THE UNDERWRITER</span>
                      <span className="v4-empty-step-v">Marks it from the file, every adjustment named
                        and every gap stated.</span>
                    </div>
                    <div className="v4-empty-step">
                      <span className="v4-empty-step-k">3 · YOUR BOOK</span>
                      <span className="v4-empty-step-v">The mark lands here without a reload, and I
                        email you when it does.</span>
                    </div>
                  </div>
                </div>
              ) : (
                <Book book={desk.book} onOpen={setOpenRef} />
              )}
            </div>
          </div>

          {!desk.empty && (
            <div className="v4-board" data-testid="region-decide">
              <div className="v4-board-head">
                <span className="v4-tab">NEEDS YOU</span>
                <span className="v4-hr" />
                <span className="v4-board-note" data-testid="decisions-count">
                  {decideCount > 0 ? plural(decideCount, "decision") + " waiting on you" : "nothing right now"}
                </span>
              </div>
              <div className="v4-board-panel">
                {decideCount === 0 ? (
                  <p className="panel-muted" data-testid="decisions-empty">
                    Nothing needs a human right now. Decisions the agents will not make land here, each
                    naming the building and the choice.
                  </p>
                ) : (
                  <DecisionQueue decisions={desk.decisions} onChanged={reload}
                                 buildings={desk.book.rows.map((r) => ({ building_ref: r.building_ref,
                                                                         label: r.label }))}
                                 onAddBuilding={() => setWizard(true)} />
                )}
              </div>
            </div>
          )}
        </main>
      </div>

      {openRef && <BuildingPanel buildingRef={openRef} onClose={() => setOpenRef(null)}
                                 onRemoved={reload}
                                 seed={(desk.book.rows || []).find((r) => r.building_ref === openRef)} />}
      {wizard && <IntakeWizard onClose={() => setWizard(false)} onChanged={reload} />}
      {settings && <Settings onClose={() => setSettings(false)} />}
    </div>
  );
}
